import React, { useState, useRef, useEffect } from 'react';
import { Form, Col } from 'react-bootstrap';
import { Search, ChevronDown } from 'lucide-react';

interface InputSearchProps<T = any> {
  // Identificação e dados
  name: string;
  data: T[];
  value: T | null;
  onChange: (item: T) => void;
  onClear?: () => void;
  
  // Layout
  size?: number; // Tamanho da coluna Bootstrap (1-12)
  
  // Customização (opcional - se não passar, usa defaults baseados no name)
  label?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  
  // Funções de busca e renderização (opcionais)
  filterFn?: (item: T, searchTerm: string) => boolean;
  renderItem?: (item: T) => React.ReactNode;
  renderSelected?: (item: T) => React.ReactNode;
  getItemKey?: (item: T) => string;
  getDisplayValue?: (item: T) => string;
  
  // Controle
  showClearButton?: boolean;
  maxHeight?: string;
}

// Configurações padrão baseadas no nome
const getDefaultConfig = (name: string) => {
  const configs: Record<string, any> = {
    cliente: {
      label: 'Cliente',
      placeholder: 'Buscar cliente por nome, telefone ou CPF...',
      emptyMessage: 'Digite para buscar clientes',
      noResultsMessage: 'Nenhum cliente encontrado',
      filterFn: (item: any, term: string) => {
        const searchLower = term.toLowerCase();
        return (
          item.nome?.toLowerCase().includes(searchLower) ||
          item.telefone?.includes(term) ||
          item.cpf?.includes(term)
        );
      },
      renderItem: (item: any) => (
        <>
          <div style={{ fontWeight: '500', color: '#333' }}>{item.nome}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {item.telefone} {item.cpf && `• ${item.cpf}`}
          </div>
        </>
      ),
      renderSelected: (item: any) => (
        <>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#0EA5E9' }}>
            Cliente selecionado: {item.nome}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {item.telefone} {item.cpf && `• ${item.cpf}`}
          </div>
        </>
      ),
      getItemKey: (item: any) => item.id || '',
      getDisplayValue: (item: any) => item.nome || ''
    },
    produto: {
      label: 'Adicionar Produto',
      placeholder: 'Buscar por código, título, tamanho ou cor...',
      emptyMessage: 'Digite para buscar produtos',
      noResultsMessage: 'Nenhum produto encontrado',
      filterFn: (item: any, term: string) => {
        if (item.quantidade <= 0) return false;
        const searchLower = term.toLowerCase();
        return (
          item.titulo?.toLowerCase().includes(searchLower) ||
          item.tamanho?.toLowerCase().includes(searchLower) ||
          item.cor?.toLowerCase().includes(searchLower) ||
          item.codigoProduto?.toLowerCase().includes(searchLower)
        );
      },
      renderItem: (item: any) => (
        <>
          <div className="d-flex align-items-center gap-2">
            {item.codigoProduto && (
              <span style={{ 
                fontSize: '12px', 
                fontWeight: '600',
                color: '#0EA5E9',
                fontFamily: 'monospace',
                backgroundColor: '#e0f2fe',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                {item.codigoProduto}
              </span>
            )}
            <div style={{ fontWeight: '500', color: '#333' }}>{item.titulo}</div>
          </div>
          <div style={{ fontSize: '14px', color: '#666', marginTop: '4px' }}>
            {item.tamanho} • {item.cor}
          </div>
          <div style={{ fontSize: '12px', color: '#28a745', fontWeight: '500', marginTop: '4px' }}>
            Disponível: {item.quantidade}
          </div>
        </>
      ),
      getItemKey: (item: any) => `${item.id}-${item.tamanho}-${item.cor}`,
      getDisplayValue: () => '' // Produtos não mostram valor após seleção
    },
    funcionario: {
      label: 'Funcionário',
      placeholder: 'Buscar funcionário por nome, email ou CPF...',
      emptyMessage: 'Digite para buscar funcionários',
      noResultsMessage: 'Nenhum funcionário encontrado',
      filterFn: (item: any, term: string) => {
        const searchLower = term.toLowerCase();
        return (
          item.nome?.toLowerCase().includes(searchLower) ||
          item.email?.toLowerCase().includes(searchLower) ||
          item.cpf?.includes(term)
        );
      },
      renderItem: (item: any) => (
        <>
          <div style={{ fontWeight: '500', color: '#333' }}>{item.nome}</div>
          <div style={{ fontSize: '14px', color: '#666' }}>
            {item.email} {item.cpf && `• ${item.cpf}`}
          </div>
        </>
      ),
      renderSelected: (item: any) => (
        <>
          <div style={{ fontSize: '14px', fontWeight: '500', color: '#0EA5E9' }}>
            Funcionário selecionado: {item.nome}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {item.email}
          </div>
        </>
      ),
      getItemKey: (item: any) => item.id || '',
      getDisplayValue: (item: any) => item.nome || ''
    }
  };

  return configs[name] || {
    label: name.charAt(0).toUpperCase() + name.slice(1),
    placeholder: `Buscar ${name}...`,
    emptyMessage: `Digite para buscar ${name}`,
    noResultsMessage: `Nenhum ${name} encontrado`,
    filterFn: (item: any, term: string) => {
      return JSON.stringify(item).toLowerCase().includes(term.toLowerCase());
    },
    renderItem: (item: any) => (
      <div style={{ fontWeight: '500', color: '#333' }}>
        {item.nome || item.titulo || item.name || JSON.stringify(item)}
      </div>
    ),
    getItemKey: (item: any) => item.id || Math.random().toString(),
    getDisplayValue: (item: any) => item.nome || item.titulo || item.name || ''
  };
};

function InputSearch<T = any>({
  name,
  data,
  value,
  onChange,
  onClear,
  size = 6,
  label,
  placeholder,
  required = false,
  helperText,
  filterFn,
  renderItem,
  renderSelected,
  getItemKey,
  getDisplayValue,
  showClearButton = true,
  maxHeight = '200px'
}: InputSearchProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Pegar configuração padrão
  const config = getDefaultConfig(name);

  // Usar funções customizadas ou defaults
  const filter = filterFn || config.filterFn;
  const render = renderItem || config.renderItem;
  const renderSel = renderSelected || config.renderSelected;
  const getKey = getItemKey || config.getItemKey;
  const getDisplay = getDisplayValue || config.getDisplayValue;

  // Atualizar searchTerm quando value mudar (mas não durante edição)
  useEffect(() => {
    if (!isEditing) {
      if (value) {
        setSearchTerm(getDisplay(value));
      } else {
        setSearchTerm('');
      }
    }
  }, [value, getDisplay, isEditing]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrar dados
  const filteredData = data.filter(item => filter(item, searchTerm));

  const handleSelect = (item: T) => {
    setIsEditing(false);
    onChange(item);
    setSearchTerm(getDisplay(item));
    setShowDropdown(false);
  };

  const handleClear = () => {
    if (onClear) {
      setIsEditing(false);
      onClear();
      setSearchTerm('');
    }
  };

  return (
    <Col md={size}>
      <Form.Group className="mb-3">
        <Form.Label>
          {label || config.label} {required && '*'}
        </Form.Label>
        
        <div ref={containerRef} style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <Form.Control
              type="text"
              placeholder={placeholder || config.placeholder}
              value={searchTerm}
              onChange={(e) => {
                setIsEditing(true);
                setSearchTerm(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => {
                setIsEditing(true);
                setShowDropdown(true);
              }}
              onBlur={() => {
                // Pequeno delay para permitir clique no dropdown
                setTimeout(() => setIsEditing(false), 200);
              }}
              style={{
                paddingRight: '80px',
                borderRadius: '8px',
                border: value ? '2px solid #0EA5E9' : '1px solid #ced4da'
              }}
            />
            
            <div style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              <Search size={16} color="#666" />
              {showClearButton && value && onClear && (
                <button
                  type="button"
                  onClick={handleClear}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#dc3545',
                    cursor: 'pointer',
                    padding: '2px',
                    fontSize: '20px',
                    lineHeight: 1
                  }}
                  title="Limpar seleção"
                >
                  ×
                </button>
              )}
              <ChevronDown size={16} color="#666" />
            </div>
          </div>

          {/* Dropdown de resultados */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: 'white',
              border: '1px solid #ced4da',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              maxHeight,
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }}>
              {filteredData.length > 0 ? (
                filteredData.map(item => (
                  <div
                    key={getKey(item)}
                    onClick={() => handleSelect(item)}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                  >
                    {render(item)}
                  </div>
                ))
              ) : (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#666',
                  fontSize: '14px'
                }}>
                  {searchTerm ? config.noResultsMessage : config.emptyMessage}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Item selecionado */}
        {value && renderSel && (
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#e3f2fd',
            borderRadius: '6px',
            border: '1px solid #0EA5E9'
          }}>
            {renderSel(value)}
          </div>
        )}

        {/* Helper text */}
        {helperText && (
          <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
            {helperText}
          </small>
        )}
      </Form.Group>
    </Col>
  );
}

export default InputSearch;

